import {
  isOrgFeatureMovable,
  isOrgFeatureOn,
  type OrgFeatureFlag,
  type SetOrgFeatureFlagsRequest,
} from '@/definitions/featureFlags'

/**
 * Turn the switches the operator moved into the two lists the endpoint takes.
 *
 * Only genuine differences are sent. A switch flicked and flicked back is not a
 * change, and a flag nobody touched is absent from the request entirely — which
 * is what keeps a stale page from rewriting a flag somebody else just moved.
 *
 * Flags with no switch are dropped even if a value somehow reached them: the
 * endpoint would refuse the change anyway, and sending it would put a misleading
 * outcome in the response.
 */
export const buildFlagChanges = (
  flags: OrgFeatureFlag[],
  staged: Record<string, boolean>
): SetOrgFeatureFlagsRequest => {
  const enable: string[] = []
  const disable: string[] = []

  for (const flag of flags) {
    const desired = staged[flag.name]
    if (desired === undefined || !isOrgFeatureMovable(flag.state)) {
      continue
    }
    if (desired === isOrgFeatureOn(flag.state)) {
      continue
    }
    if (desired) {
      enable.push(flag.name)
    } else {
      disable.push(flag.name)
    }
  }

  return { enable, disable }
}

/** Whether there is anything to save. */
export const hasFlagChanges = (changes: SetOrgFeatureFlagsRequest): boolean =>
  changes.enable.length > 0 || changes.disable.length > 0

/**
 * The names the save asked to move that the stored states say did not move.
 *
 * The endpoint answers with every flag's state read back *after* the write, so a
 * name it would not move comes back carrying the state that explains why rather
 * than the one that was asked for. Comparing the two is what keeps a refusal from
 * being reported as a successful save.
 */
export const unmovedFlags = (
  stored: OrgFeatureFlag[],
  changes: SetOrgFeatureFlagsRequest
): string[] => {
  const stateByName = new Map(stored.map((flag) => [flag.name, flag.state]))

  return [
    ...changes.enable.map((name) => ({ name, wanted: true })),
    ...changes.disable.map((name) => ({ name, wanted: false })),
  ]
    .filter(({ name, wanted }) => {
      const state = stateByName.get(name)
      // A name missing from the response is as unaccounted for as one that came
      // back in the wrong state.
      return state === undefined || isOrgFeatureOn(state) !== wanted
    })
    .map(({ name }) => name)
}
